/**
 * Especificação PMO (Especificação.pptx) — presets, filtros e composição de JQL.
 */

import { formatMonthYear, getMonthYearBound } from '../../utils/monthYearParse';

export type ProjectsAccessLevel = 'np0' | 'np3';

export type PmoCarteiraGroup = 'operacional' | 'sem_receita' | 'interno';

export interface PmoCarteiraOption {
  id: string;
  label: string;
  jqlFragment: string;
}

export interface PmoPresetSpec {
  id: string;
  name: string;
  context: string;
  accessLevel: ProjectsAccessLevel;
  baseJql: string;
  kpiIds: string[];
  columnIds: string[];
  carteiraGroup: PmoCarteiraGroup;
}

export type PmoReportScopeFilter = 'carteira' | 'planilha_sap_nsap' | 'planilha_export_worklogs';

export interface PmoStructuredFiltersState {
  periodStart: string;
  periodEnd: string;
  carteiraId: string;
  /** Valor exato do select Jira "Classificação do Atendimento" (customfield_11434). Independente de carteira. */
  classificationValue: string;
  statusFilter: string;
  vigencia: string;
  phase: string;
  /** Modo relatório Compras-e — KPIs comparativos; não altera HH clássicos. */
  reportMode: boolean;
  reportScope: PmoReportScopeFilter;
}

export const PMO_CARTEIRA_FIELD_DEFAULT = 'customfield_11434';

/** Campo Jira Observações / Plano de Ação (edição inline no dashboard). */
export const PMO_OBSERVATION_FIELD_DEFAULT = 'customfield_11263';

/** Opções reais do campo Classificação do Atendimento (lista Beatris / Jira + COMPRAS-E). */
export const PMO_CLASSIFICACAO_ATENDIMENTO_OPTIONS: string[] = [
  'CONTRATO - PORTAL DE COMPRAS',
  'MELHORIAS OPERACIONAIS - PORTAL DE COMPRAS',
  'COMPRAS-E',
  'INOVAÇÃO - FASE 2 IMPLEMENTAÇÃO E AVALIAÇÃO DO MVP0',
  'INOVAÇÃO - FASE 1 CONCEPÇÃO E VALIDAÇÃO DA IDEIA',
  'CONTRATO - CADASTRO DE FORNECEDORES',
  'CONTRATO - ZPQS do DPV',
  'MINHA PETRONECT',
  'MELHORIAS OPERACIONAIS - PORTAL CADASTRO',
  'MELHORIAS OPERACIONAIS - TRANSPETRO',
  'CONTRATO - TRANSPETRO',
  'MELHORIAS OPERACIONAIS - AUTOMAÇÕES',
  'CONTRATO - LEVEL',
  'INOVAÇÃO',
];

/**
 * Classificações do dropdown por Carteira (planilha Beatris: Carteira → Classificação).
 * Compras-E: opção COMPRAS-E; o projeto CMPE continua entrando pela regra da carteira
 * (não é valor do campo Classificação).
 */
export const PMO_CARTEIRA_CLASSIFICACAO_OPTIONS: Record<string, string[]> = {
  portal_compras: [
    'CONTRATO - PORTAL DE COMPRAS',
    'MELHORIAS OPERACIONAIS - PORTAL DE COMPRAS',
  ],
  cadastro: [
    'CONTRATO - CADASTRO DE FORNECEDORES',
    'MELHORIAS OPERACIONAIS - PORTAL CADASTRO',
  ],
  compras_e: ['COMPRAS-E'],
  inovacao: [
    'INOVAÇÃO - FASE 2 IMPLEMENTAÇÃO E AVALIAÇÃO DO MVP0',
    'INOVAÇÃO - FASE 1 CONCEPÇÃO E VALIDAÇÃO DA IDEIA',
    'INOVAÇÃO - FASE 1 CONCEPÇÃO  E VALIDAÇÃO DA IDEIA',
    'INOVAÇÃO',
  ],
  zpqs: ['CONTRATO - ZPQS do DPV'],
  minha_petronect: ['MINHA PETRONECT'],
  transpetro: [
    'CONTRATO - TRANSPETRO',
    'MELHORIAS OPERACIONAIS - TRANSPETRO',
  ],
  automacoes: ['MELHORIAS OPERACIONAIS - AUTOMAÇÕES'],
  level: ['CONTRATO - LEVEL'],
  // Agrupadores da visão Sem Receita (não estão na planilha linha a linha).
  melhorias: [
    'MELHORIAS OPERACIONAIS - TRANSPETRO',
    'MELHORIAS OPERACIONAIS - AUTOMAÇÕES',
  ],
  contrato: [
    'CONTRATO - ZPQS do DPV',
    'CONTRATO - TRANSPETRO',
    'CONTRATO - LEVEL',
  ],
  // Continuidade = projeto CCO; sem classificação Beatris específica.
  continuidade_operacional: [],
};

/** Opções de Classificação exibidas no filtro conforme a Carteira selecionada. */
export const classificationOptionsForCarteira = (carteiraId: string): string[] => {
  const key = String(carteiraId || '').trim();
  if (!key) return [...PMO_CLASSIFICACAO_ATENDIMENTO_OPTIONS];

  if (!Object.prototype.hasOwnProperty.call(PMO_CARTEIRA_CLASSIFICACAO_OPTIONS, key)) {
    // Carteira sem mapa (ex.: internas): mantém lista completa.
    return [...PMO_CLASSIFICACAO_ATENDIMENTO_OPTIONS];
  }

  const mapped = PMO_CARTEIRA_CLASSIFICACAO_OPTIONS[key] || [];
  const canonical = new Set(PMO_CLASSIFICACAO_ATENDIMENTO_OPTIONS.map((item) => item.trim()));
  // Ordem da planilha/carteira; ignora variante com espaço duplo na UI.
  const seen = new Set<string>();
  const options: string[] = [];
  for (const raw of mapped) {
    const option = String(raw || '').trim();
    if (!option || seen.has(option) || !canonical.has(option)) continue;
    // Não listar a variante com espaço duplo da Fase 1.
    if (option.includes('  ')) continue;
    seen.add(option);
    options.push(option);
  }
  return options;
};


/** Status do workflow de História (PORTAL) — filtro Fase usa match no status da História. */
export const PMO_PHASES = [
  'Em Refinamento',
  'Refinado',
  'Em aprovação da Demanda',
  'Iniciar atendimento',
  'Desenho Funcional',
  'Desenho Técnico',
  'Em desenvolvimento',
  'Desenvolvido',
  'Teste Funcional',
  'Em Homologação',
  'Homologada',
  'Release',
  'Estabilização',
  'Transição de Serviço',
  'Pesquisa de Satisfação',
  'Avaliação GPRO',
  'Concluído',
];

export const PMO_GO_LIVE_PLANNED_FIELD_DEFAULT = 'customfield_11419';

/** Aliases opcionais fase → cláusula JQL (quando o compose-jql for usado). */
const PMO_PHASE_JQL_ALIASES: Record<string, string> = {};

/** Carteiras da planilha Beatris (Carteira → Classificação) — visões operacionais. */
export const PMO_CARTEIRAS_OPERACIONAIS: PmoCarteiraOption[] = [
  {
    id: 'portal_compras',
    label: 'Portal de Compras',
    jqlFragment:
      'customfield_11434 in ("CONTRATO - PORTAL DE COMPRAS", "MELHORIAS OPERACIONAIS - PORTAL DE COMPRAS")',
  },
  {
    id: 'cadastro',
    label: 'Portal Cadastro',
    jqlFragment:
      'customfield_11434 in ("CONTRATO - CADASTRO DE FORNECEDORES", "MELHORIAS OPERACIONAIS - PORTAL CADASTRO")',
  },
  {
    id: 'compras_e',
    label: 'Compras-E',
    jqlFragment:
      '(project = "COMPRAS-E" OR project = CMPE OR customfield_11434 = "COMPRAS-E" OR summary ~ "Compras-e")',
  },
  {
    id: 'inovacao',
    label: 'Inovação',
    jqlFragment:
      'customfield_11434 in ("INOVAÇÃO", "INOVAÇÃO - FASE 1 CONCEPÇÃO E VALIDAÇÃO DA IDEIA", "INOVAÇÃO - FASE 1 CONCEPÇÃO  E VALIDAÇÃO DA IDEIA", "INOVAÇÃO - FASE 2 IMPLEMENTAÇÃO E AVALIAÇÃO DO MVP0")',
  },
  {
    id: 'zpqs',
    label: 'ZPQS',
    jqlFragment: 'customfield_11434 = "CONTRATO - ZPQS do DPV"',
  },
  {
    id: 'minha_petronect',
    label: 'Minha Petronect',
    jqlFragment: 'customfield_11434 = "MINHA PETRONECT"',
  },
  {
    id: 'transpetro',
    label: 'Transpetro',
    jqlFragment: 'customfield_11434 in ("CONTRATO - TRANSPETRO", "MELHORIAS OPERACIONAIS - TRANSPETRO")',
  },
  {
    id: 'automacoes',
    label: 'Automações',
    jqlFragment: 'customfield_11434 = "MELHORIAS OPERACIONAIS - AUTOMAÇÕES"',
  },
  {
    id: 'level',
    label: 'Contrato Level',
    jqlFragment: 'customfield_11434 = "CONTRATO - LEVEL"',
  },
];

export const PMO_CARTEIRAS_SEM_RECEITA: PmoCarteiraOption[] = [
  {
    id: 'melhorias',
    label: 'Melhorias',
    jqlFragment:
      'customfield_11434 in ("MELHORIAS OPERACIONAIS - TRANSPETRO", "MELHORIAS OPERACIONAIS - AUTOMAÇÕES")',
  },
  {
    id: 'inovacao',
    label: 'Inovação',
    jqlFragment:
      'customfield_11434 in ("INOVAÇÃO", "INOVAÇÃO - FASE 1 CONCEPÇÃO E VALIDAÇÃO DA IDEIA", "INOVAÇÃO - FASE 1 CONCEPÇÃO  E VALIDAÇÃO DA IDEIA", "INOVAÇÃO - FASE 2 IMPLEMENTAÇÃO E AVALIAÇÃO DO MVP0")',
  },
  { id: 'minha_petronect', label: 'Minha Petronect', jqlFragment: 'customfield_11434 = "MINHA PETRONECT"' },
  { id: 'contrato', label: 'Contrato (ZPQS/Transpetro/Level)', jqlFragment: 'customfield_11434 = "CONTRATO -"' },
  { id: 'zpqs', label: 'ZPQS do DPV', jqlFragment: 'customfield_11434 = "CONTRATO - ZPQS do DPV"' },
  {
    id: 'transpetro',
    label: 'Transpetro',
    jqlFragment: 'customfield_11434 in ("MELHORIAS OPERACIONAIS - TRANSPETRO", "CONTRATO - TRANSPETRO")',
  },
  { id: 'level', label: 'Contrato Level', jqlFragment: 'customfield_11434 = "CONTRATO - LEVEL"' },
  { id: 'automacoes', label: 'Automações', jqlFragment: 'customfield_11434 = "MELHORIAS OPERACIONAIS - AUTOMAÇÕES"' },
  {
    id: 'continuidade_operacional',
    label: 'Melhorias de Continuidade Operacional',
    jqlFragment: 'project = CCO',
  },
];

/** Classificações internas (fora da lista Beatris). Filtro opcional — não puxa nas visões operacionais. */
export const PMO_CARTEIRAS_INTERNAS: PmoCarteiraOption[] = [
  { id: 'despesas_gtec', label: 'Despesas - GTEC', jqlFragment: 'customfield_11434 = "DESPESAS - GTEC"' },
  { id: 'despesas_rh', label: 'Despesas - RH', jqlFragment: 'customfield_11434 = "DESPESAS - RH"' },
  { id: 'despesas_gserv', label: 'Despesas - GSERV', jqlFragment: 'customfield_11434 = "DESPESAS - GSERV"' },
  { id: 'despesas_dar', label: 'Despesas - DAR', jqlFragment: 'customfield_11434 = "DESPESAS - DAR"' },
  { id: 'apoio_operacao', label: 'Apoio à Operação', jqlFragment: 'customfield_11434 = "APOIO A OPERAÇÃO"' },
  {
    id: 'ie_seguranca_infra',
    label: 'IE - Segurança da Informação e Infraestrutura',
    jqlFragment: 'customfield_11434 = "IE - SEGURANÇA DA INFORMAÇÃO E INFRAESTRUTURA"',
  },
  { id: 'ie_datahouse', label: 'IE - Datahouse Petronect', jqlFragment: 'customfield_11434 = "IE - DATAHOUSE PETRONECT"' },
  { id: 'seguranca_informacao', label: 'Segurança da Informação', jqlFragment: 'customfield_11434 = "SEGURANÇA DA INFORMAÇÃO"' },
  {
    id: 'eventos_agendas',
    label: 'Eventos e Agendas Corporativas',
    jqlFragment: 'customfield_11434 = "EVENTOS E AGENDAS CORPORATIVAS"',
  },
  { id: 'iniciativa_estrategica', label: 'Iniciativa Estratégica', jqlFragment: 'customfield_11434 = "INICIATIVA ESTRATÉGICA"' },
  { id: 'outros_investimentos', label: 'Outros Investimentos', jqlFragment: 'customfield_11434 = "OUTROS INVESTIMENTOS"' },
  { id: 'poc_gserv', label: 'POC - Gserv', jqlFragment: 'customfield_11434 = "POC - Gserv"' },
  {
    id: 'automacao_cotacoes',
    label: 'Melhorias - Automação de Cotações',
    jqlFragment: 'customfield_11434 = "MELHORIAS OPERACIONAIS - AUTOMAÇÃO DE COTAÇÕES"',
  },
];

/** Classificação executiva PMO (slide 7). Atraso ≡ Crítico na legenda visual. */
export const PMO_STATUS_FILTERS = [
  { id: 'atraso', label: 'Atraso (Crítico)' },
  { id: 'em_risco', label: 'Em risco' },
  { id: 'no_prazo', label: 'No prazo' },
];

export const PMO_VIGENCIA_FILTERS = [
  { id: 'em_andamento', label: 'Em andamento' },
  { id: 'concluido', label: 'Concluído' },
  { id: 'cancelado', label: 'Cancelado' },
];

const BASE_JQL =
  'project = PORTAL AND issuetype = "História" AND resolution is empty ORDER BY updated DESC, created DESC';

/** Pool amplo de Histórias — carteira, período e vigência ficam nos filtros PMO da UI. */
export const PMO_GLOBAL_BASE_JQL =
  'project in (PORTAL, CMPE, "COMPRAS-E", CDEN, CADASTRO, DATALAKE, CADAST, DEMANDA, CCO) '
  + 'AND issuetype = História '
  + 'AND (resolution is EMPTY OR resolved >= "2025-01-01") '
  + 'ORDER BY updated DESC, created DESC';

export const PMO_DEFAULT_BASE_JQL = BASE_JQL;

/** Visão que espelha a tela Compras-e (metodologia do relatório SAP+NSAP). */
export const COMPRAS_E_COMPARATIVO_PRESET_ID = 'pmo-preset-compras-e-relatorio';

/**
 * Pool da visão Compras-e: qualquer item que possa ser pai de um apontamento.
 * O relatório Compras-e sobe do filho para o pai sem olhar o tipo, então limitar
 * a "História" perdia Epics/Bugs/Melhorias do CMPE que têm filhos com hora.
 */
export const COMPRAS_E_COMPARATIVO_BASE_JQL =
  'project in (PORTAL, CMPE, "COMPRAS-E", CDEN, CADASTRO, DATALAKE, CADAST, DEMANDA, CCO) '
  + 'AND issuetype not in subTaskIssueTypes() '
  + 'AND (resolution is EMPTY OR resolved >= "2025-01-01") '
  + 'ORDER BY updated DESC, created DESC';

export const PMO_PRESET_SPECS: PmoPresetSpec[] = [
  {
    id: 'pmo-preset-global',
    name: 'Escopo global (multi-projeto)',
    context: 'Pool amplo de Histórias (PORTAL, CMPE, Compras-e, etc.) — use filtros PMO para carteira e período.',
    accessLevel: 'np0',
    baseJql: PMO_GLOBAL_BASE_JQL,
    kpiIds: [
      'total_items',
      'hh_vendido_total',
      'hh_real_total',
      'consumo_hh_percent',
      'fases_no_prazo',
      'concluidos',
      'pontos_criticos',
      'projetos_fora_do_prazo',
    ],
    columnIds: [
      'project',
      'go_live_planned',
      'go_live_real',
      'hh_vendido_seconds',
      'hh_real_seconds',
      'status',
      PMO_OBSERVATION_FIELD_DEFAULT,
    ],
    carteiraGroup: 'operacional',
  },
  {
    id: COMPRAS_E_COMPARATIVO_PRESET_ID,
    name: 'Compras-e — comparativo relatório',
    context: 'KPIs de período na metodologia do relatório Compras-e (SAP+NSAP no Jira) — mesmos números da tela Compras-e. Os HH clássicos (acumulado Jira) ficam desligados por padrão; habilite na lista de cards se precisar comparar.',
    accessLevel: 'np0',
    baseJql: COMPRAS_E_COMPARATIVO_BASE_JQL,
    kpiIds: [
      'total_items',
      'hh_real_worklog_period',
      'hh_vendido_relatorio',
      'consumo_hh_relatorio_percent',
    ],
    columnIds: [
      'project',
      'go_live_planned',
      'go_live_real',
      'hh_vendido_seconds',
      'hh_real_seconds',
      'status',
      PMO_OBSERVATION_FIELD_DEFAULT,
    ],
    carteiraGroup: 'operacional',
  },
  {
    id: 'pmo-preset-performance',
    name: 'Performance (HH, Fase, Status)',
    context: 'Visão operacional NP0 — acompanhamento de horas, fase e status.',
    accessLevel: 'np0',
    baseJql: BASE_JQL,
    kpiIds: [
      'total_items',
      'hh_vendido_total',
      'hh_real_total',
      'consumo_hh_percent',
      'fases_no_prazo',
      'concluidos',
      'pontos_criticos',
      'projetos_fora_do_prazo',
    ],
    columnIds: [
      'project',
      'go_live_planned',
      'go_live_real',
      'hh_vendido_seconds',
      'hh_real_seconds',
      'status',
      PMO_OBSERVATION_FIELD_DEFAULT,
    ],
    carteiraGroup: 'operacional',
  },
  {
    id: 'pmo-preset-status-financeiro',
    name: 'Status Financeiro',
    context: 'Visão NP3 — receita, margem e custo consolidado.',
    accessLevel: 'np3',
    baseJql: BASE_JQL,
    kpiIds: [
      'total_items',
      'venda_total',
      'margem_venda_comercial_total',
      'custo_total',
      'margem_pos_custo_total',
      'ponto_critico_os',
    ],
    columnIds: [
      'project',
      'hh_vendido_seconds',
      'hh_real_seconds',
      '__own_cost_cents',
      '__third_party_cost_cents',
      '__issue_cost_cents',
      '__commercial_margin_cents',
      '__sale_margin_cents',
      '__sale_value_cents',
      'status',
      PMO_OBSERVATION_FIELD_DEFAULT,
    ],
    carteiraGroup: 'operacional',
  },
  {
    id: 'pmo-preset-custo-hh',
    name: 'Custo - HH',
    context: 'Visão NP3 — custo por hora e mix próprio/terceiro.',
    accessLevel: 'np3',
    baseJql: BASE_JQL,
    kpiIds: ['custo_total', 'hh_realizado_total', 'horas_realizadas_mix', 'custo_mix', 'custo_previsto_total'],
    columnIds: [
      'project',
      'hh_vendido_seconds',
      'hh_real_seconds',
      '__own_cost_cents',
      '__third_party_cost_cents',
      '__issue_cost_cents',
      'status',
      PMO_OBSERVATION_FIELD_DEFAULT,
    ],
    carteiraGroup: 'operacional',
  },
  {
    id: 'pmo-preset-sem-receita',
    name: 'Projetos sem Receita',
    context: 'Visão NP3 — melhorias e inovação sem receita de venda.',
    accessLevel: 'np3',
    baseJql: BASE_JQL,
    kpiIds: ['total_items', 'hh_vendido_total', 'hh_real_total', 'custo_total', 'concluidos'],
    columnIds: [
      'project',
      'go_live_planned',
      'go_live_real',
      'hh_vendido_seconds',
      'hh_real_seconds',
      'status',
      PMO_OBSERVATION_FIELD_DEFAULT,
    ],
    carteiraGroup: 'sem_receita',
  },
  {
    id: 'pmo-preset-interno',
    name: 'Interno / Despesas',
    context: 'Filtro opcional por Classificação do Atendimento interna (DESPESAS, IE, Apoio). Não entra nas visões operacionais padrão.',
    accessLevel: 'np3',
    baseJql: BASE_JQL,
    kpiIds: ['total_items', 'hh_vendido_total', 'hh_real_total', 'custo_total', 'concluidos'],
    columnIds: ['project', 'go_live_planned', 'go_live_real', 'hh_vendido_seconds', 'hh_real_seconds', 'status'],
    carteiraGroup: 'interno',
  },
];

export const EMPTY_STRUCTURED_FILTERS: PmoStructuredFiltersState = {
  periodStart: '',
  periodEnd: '',
  carteiraId: '',
  classificationValue: '',
  statusFilter: '',
  vigencia: '',
  phase: '',
  reportMode: false,
  reportScope: 'carteira',
};

export const PMO_COMPARATIVO_PERIOD_START = '01/2025';

/**
 * Filtros sugeridos ao abrir a visão comparativa Compras-e.
 * O Até acompanha o mês corrente, igual ao padrão da tela Compras-e.
 */
export const pmoComparativoDefaultFilters = (now: Date = new Date()): PmoStructuredFiltersState => ({
  ...EMPTY_STRUCTURED_FILTERS,
  periodStart: PMO_COMPARATIVO_PERIOD_START,
  periodEnd: formatMonthYear(now.getMonth() + 1, now.getFullYear()),
  carteiraId: 'compras_e',
  reportMode: true,
  reportScope: 'planilha_export_worklogs',
});

/**
 * Reconcilia os cards de uma visão Compras-e já salva com a spec atual do preset:
 * a lista de ids vem sempre da spec, preservando títulos renomeados pelo usuário.
 */
export const reconcileComprasEPresetKpis = (
  spec: PmoPresetSpec,
  saved: Array<{ id: string; title?: string }>,
): Array<{ id: string; title?: string }> =>
  spec.kpiIds.map((id) => {
    const previous = saved.find((item) => item.id === id);
    return previous?.title ? { id, title: previous.title } : { id };
  });

const escapeJql = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const toWorklogIso = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const carteiraCatalog = (group: PmoCarteiraGroup): PmoCarteiraOption[] => {
  if (group === 'sem_receita') return PMO_CARTEIRAS_SEM_RECEITA;
  if (group === 'interno') return PMO_CARTEIRAS_INTERNAS;
  return PMO_CARTEIRAS_OPERACIONAIS;
};

const resolveCarteiraFragment = (carteiraId: string, group: PmoCarteiraGroup): string | null => {
  if (!carteiraId.trim()) return null;
  const item = carteiraCatalog(group).find((entry) => entry.id === carteiraId);
  return item?.jqlFragment || null;
};

/** Utilitário legado de composição JQL — filtros PMO da UI filtram linhas já carregadas. */
export const composePmoJql = (
  baseJql: string,
  filters: PmoStructuredFiltersState,
  carteiraGroup: PmoCarteiraGroup,
): string => {
  const q = (baseJql || '').trim();
  if (!q) return '';

  const orderMatch = q.match(/\border\s+by\b/i);
  const orderBy = orderMatch ? q.slice(orderMatch.index) : '';
  const baseFilter = orderMatch ? q.slice(0, orderMatch.index).trim() : q;

  const clauses: string[] = [];

  const carteiraFragment = resolveCarteiraFragment(filters.carteiraId, carteiraGroup);
  if (carteiraFragment) clauses.push(`(${carteiraFragment})`);

  const periodStart = getMonthYearBound(filters.periodStart, 'start');
  const periodEnd = getMonthYearBound(filters.periodEnd, 'end');
  if (periodStart) {
    clauses.push(`worklogDate >= "${toWorklogIso(periodStart)}"`);
  }
  if (periodEnd) {
    clauses.push(`worklogDate <= "${toWorklogIso(periodEnd)}"`);
  }

  if (filters.vigencia === 'em_andamento') clauses.push('resolution is empty');
  if (filters.vigencia === 'concluido') clauses.push('resolution is not empty');
  if (filters.vigencia === 'cancelado') clauses.push('(status ~ "Cancel" OR resolution = Canceled)');

  if (filters.phase.trim()) {
    const phase = filters.phase.trim();
    const alias = PMO_PHASE_JQL_ALIASES[phase];
    clauses.push(alias || `status = "${escapeJql(phase)}"`);
  }

  if (filters.statusFilter === 'atraso') {
    clauses.push(
      `((duedate < now()) OR ("${PMO_GO_LIVE_PLANNED_FIELD_DEFAULT}" < now())) AND resolution is empty`,
    );
  }
  if (filters.statusFilter === 'em_risco') clauses.push('(status ~ "risco" OR status ~ "Risco")');
  if (filters.statusFilter === 'no_prazo') {
    clauses.push(
      `((duedate >= now() OR duedate is empty) AND ("${PMO_GO_LIVE_PLANNED_FIELD_DEFAULT}" >= now() OR "${PMO_GO_LIVE_PLANNED_FIELD_DEFAULT}" is empty)) AND resolution is empty AND NOT (status ~ "risco" OR status ~ "Risco")`,
    );
  }

  if (!clauses.length) return `${baseFilter} ${orderBy}`.trim();
  return `(${baseFilter}) AND (${clauses.join(' AND ')}) ${orderBy}`.trim();
};

export const resolveProjectsAccessLevel = (user?: {
  role?: string;
  can_access_projects?: boolean;
  projects_access_level?: string | null;
}): ProjectsAccessLevel | null => {
  if (!user) return null;
  if (user.role === 'admin') return 'np3';
  if (!user.can_access_projects) return null;
  const level = String(user.projects_access_level || '').trim().toLowerCase();
  if (level === 'np3') return 'np3';
  if (level === 'np0') return 'np0';
  if (user.role === 'analista') return 'np3';
  return 'np0';
};

export const canViewPreset = (
  preset: PmoPresetSpec,
  accessLevel: ProjectsAccessLevel | null,
): boolean => {
  if (!accessLevel) return false;
  if (accessLevel === 'np3') return true;
  return preset.accessLevel === 'np0';
};

export const resolveFilterBaseJql = (presetSpecId?: string): string => {
  if (presetSpecId) {
    const preset = PMO_PRESET_SPECS.find((item) => item.id === presetSpecId);
    if (preset?.baseJql?.trim()) return preset.baseJql.trim();
  }
  return PMO_DEFAULT_BASE_JQL;
};

export const buildPresetViews = (): Array<{
  id: string;
  name: string;
  context: string;
  jql: string;
  selectedColumns: string[];
  kpiConfigs: Array<{ id: string }>;
  columnWidths: Record<string, number>;
  isPreset: boolean;
  presetSpecId: string;
  carteiraGroup: PmoCarteiraGroup;
  accessLevel: ProjectsAccessLevel;
}> =>
  PMO_PRESET_SPECS.map((preset) => ({
    id: preset.id,
    name: preset.name,
    context: preset.context,
    jql: preset.baseJql,
    selectedColumns: [...preset.columnIds],
    kpiConfigs: preset.kpiIds.map((id) => ({ id })),
    columnWidths: {},
    isPreset: true,
    presetSpecId: preset.id,
    carteiraGroup: preset.carteiraGroup,
    accessLevel: preset.accessLevel,
  }));
